/**
 * Post-processing public API (asset contract §8.2). FROZEN SIGNATURES for M1. Asset-lane owned (task A7).
 * The stack (style guide §3.2, §3.5, §3.8): an MSAA HDR scene pass, the Eclipse mip-chain bloom (High: from the
 * full-res frame, Medium: half-res, Low: off), procedural lens dirt on High, the renderer's ACES + exposure output
 * transform, vignette, and combat feedback (damage edge, a ≤ 0.3 s chromatic aberration kick, boost grade).
 * Values live in post.json and the `tuning` group.
 *
 * Usage contract for the gameplay lane:
 * - `render()` replaces `renderer.render(scene, camera)` in the frame loop.
 * - `setSize()` owns renderer sizing: gameplay calls it on resize and on quality change instead of
 *   `renderer.setSize` / `setPixelRatio`, and updates `camera.aspect` itself.
 * - `setFeedback(damage, boost)`: 0..1 each, every frame (damage pulse decays on the gameplay side).
 */
import type { PerspectiveCamera, Scene, WebGPURenderer } from 'three/webgpu'
import { logger } from '../../core/logger'
import type { QualityPreset, TuningGroup } from '../contracts'
import { createPostController } from './controller'
import { loadPostData } from './data'
import { buildPostGraph } from './graph'

export interface PostPipeline {
  readonly tuning: TuningGroup
  render(): void
  setFeedback(damage: number, boost: number): void
  setSize(width: number, height: number, pixelRatio: number): void
  setPreset(preset: QualityPreset): void
  dispose(): void
}

export function createPostPipeline(
  renderer: WebGPURenderer,
  scene: Scene,
  camera: PerspectiveCamera,
  preset: QualityPreset,
): PostPipeline {
  const data = loadPostData()
  const target = buildPostGraph(renderer, scene, camera, data)
  const post = createPostController(target, data, preset, () => performance.now())
  // Compile both pipelines at load (the inactive one first) so a preset switch never builds shaders mid-game.
  if (preset === 'low') target.renderBloom()
  else target.renderPlain()
  post.render()
  logger.debug('Post pipeline ready', { preset })
  return post
}
